import { MortgageTrackRepaymentType } from "../interfaces/DTO/mortgage/mortgage";

export interface MortgagePayment {
  month: number;
  principalPayment: number;
  interestPayment: number;
  totalPayment: number;
  remainingBalance: number;
}

export interface MortgageCalculationParams {
  amount: number;
  months: number;
  annualInterestRate: number;
  repaymentType: MortgageTrackRepaymentType;
}

/**
 * מחשב החזר חודשי שווה (שפיצר)
 */
function calculateEqualMonthlyPayment(
  principal: number,
  monthlyRate: number,
  months: number
): number {
  if (monthlyRate === 0) return principal / months;
  return (principal * monthlyRate * Math.pow(1 + monthlyRate, months)) / (Math.pow(1 + monthlyRate, months) - 1);
}

/**
 * מחשב החזרי משכנתא לפי סוג ההחזר
 */
export function calculateMortgagePayments(params: MortgageCalculationParams): MortgagePayment[] {
  const { amount, months, annualInterestRate, repaymentType } = params;
  const monthlyInterestRate = annualInterestRate / 100 / 12;
  const payments: MortgagePayment[] = [];
  let remainingBalance = amount;

  switch (repaymentType) {
    case MortgageTrackRepaymentType.SPITZER: {
      // שפיצר - תשלום חודשי קבוע
      const monthlyPayment = calculateEqualMonthlyPayment(amount, monthlyInterestRate, months);

      for (let month = 1; month <= months; month++) {
        const interestPayment = remainingBalance * monthlyInterestRate;
        const principalPayment = monthlyPayment - interestPayment;
        remainingBalance = Math.max(0, remainingBalance - principalPayment);

        payments.push({
          month,
          principalPayment,
          interestPayment,
          totalPayment: monthlyPayment,
          remainingBalance,
        });
      }
      break;
    }

    case MortgageTrackRepaymentType.FIXED_PRINCIPAL: {
      // קרן קבועה - התשלום יורד
      const principalPayment = amount / months;

      for (let month = 1; month <= months; month++) {
        const interestPayment = remainingBalance * monthlyInterestRate;
        const totalPayment = principalPayment + interestPayment;
        remainingBalance = Math.max(0, remainingBalance - principalPayment);

        payments.push({
          month,
          principalPayment,
          interestPayment,
          totalPayment,
          remainingBalance,
        });
      }
      break;
    }

    case MortgageTrackRepaymentType.BULLET: {
      // בלון - רק ריבית בכל החודשים, והקרן בסוף
      for (let month = 1; month < months; month++) {
        const interestPayment = remainingBalance * monthlyInterestRate;
        payments.push({
          month,
          principalPayment: 0,
          interestPayment,
          totalPayment: interestPayment,
          remainingBalance,
        });
      }

      // החודש האחרון - ריבית + כל הקרן
      const finalInterestPayment = remainingBalance * monthlyInterestRate;
      payments.push({
        month: months,
        principalPayment: remainingBalance,
        interestPayment: finalInterestPayment,
        totalPayment: remainingBalance + finalInterestPayment,
        remainingBalance: 0,
      });
      break;
    }

    default:
      throw new Error(`Unsupported repayment type: ${repaymentType}`);
  }

  return payments;
}
